import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Collapse } from 'reactstrap';
import SidebarRun from './Sidebar.run';
import UserProfile from '../Common/UserProfile';
import Api from '../Api/Api';
import ApiLogin from '../Api/ApiLogin';
import swal from 'sweetalert';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as actions from '../../store/actions/actions';
import LogoffRun from './Logoff.run';
import * as CONST from '../Common/constants';
import RateReviewOutlinedIcon from '@material-ui/icons/RateReviewOutlined';
import JobReviewController from '../Jobs/JobReviewController';
import { makeStyles } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { getAccountSwitchConfig } from '../../utils/accountSwitchUrls';

const messages = defineMessages({
  logoutMsg: {
    id: 'Sidebar.logoutMsg',
    defaultMessage: 'Are you sure do you want to logout?'
  },
  jobRequests_noPlan: {
    id: 'Sidebar.jobRequests_noPlan',
    defaultMessage: 'Not available on your current plan. Click to know more...'
  },
  blockListUser: {
    id: 'Sidebar.blockListUser',
    defaultMessage: 'Restrictions list'
  },
  jobRequestApprovalList: {
    id: 'Sidebar.jobRequestApprovalList',
    defaultMessage: 'Approval flow list'
  }
});

const reviewIconStyles = makeStyles((theme) => ({
  root: {
    color: (props) =>
      props.active ? CONST.APP_COLORS['primary'] : CONST.APP_COLORS['gray-darker-medium'],
    fontSize: 'inherit',
    marginTop: '0.2rem',

    '&:hover': {
      color: CONST.APP_COLORS['primary']
    }
  }
}));

const StyledRateReviewOutlinedIcon = (props) => {
  const { color, displaySize, active, ...other } = props;
  const classes = reviewIconStyles({ active });

  return <RateReviewOutlinedIcon className={classes.root} {...other} />;
};

const accountSwitchCtaStyles = makeStyles(() => ({
  item: {
    borderLeft: 'none !important',
    listStyle: 'none',
    display: 'flex',
    justifyContent: 'center'
  },
  button: {
    display: 'flex !important',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    margin: '8px 12px 12px',
    padding: '8px 12px !important',
    width: '100%',
    maxWidth: 'calc(100% - 24px)',
    boxSizing: 'border-box',
    borderRadius: 999,
    backgroundColor: `${CONST.APP_COLORS.primary} !important`,
    color: `${CONST.APP_COLORS.white} !important`,
    fontWeight: '600 !important',
    fontSize: 13,
    lineHeight: 1.2,
    textAlign: 'center',
    textDecoration: 'none !important',
    boxShadow: '0 2px 6px rgba(255, 90, 138, 0.3)',
    transition: 'background-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',

    '&:hover, &:focus': {
      backgroundColor: `${CONST.APP_COLORS.pink} !important`,
      color: `${CONST.APP_COLORS.white} !important`,
      textDecoration: 'none !important',
      transform: 'translateY(-1px)',
      boxShadow: '0 3px 8px rgba(255, 90, 138, 0.36)'
    }
  },
  label: {
    color: `${CONST.APP_COLORS.white} !important`,
    margin: 0,
    display: 'inline !important',
    visibility: 'visible !important',
    opacity: '1 !important',
    fontSize: '13px !important',
    fontWeight: '600 !important',
    whiteSpace: 'nowrap'
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    border: '1px solid rgba(255, 255, 255, 0.65)'
  },
  iconFallback: {
    color: `${CONST.APP_COLORS.white} !important`,
    margin: 0
  }
}));

const sidebarBadgeLinkStyles = makeStyles(() => ({
  link: {
    display: 'flex !important',
    alignItems: 'center !important',
    '& > em': {
      flexShrink: 0
    },
    '& > span': {
      flex: '0 1 auto',
      whiteSpace: 'nowrap'
    },
    '& > .badge': {
      display: 'inline-block !important',
      float: 'none !important',
      margin: '0 0 0 8px !important',
      flexShrink: 0,
      position: 'relative',
      top: 'auto',
      right: 'auto',
      transform: 'none',
      zIndex: 1
    }
  }
}));

/** Link de sidebar com badge alinhada ao centro do item */
const SidebarBadgeLink = ({ to, title, count, iconClass, children }) => {
  const classes = sidebarBadgeLinkStyles();

  return (
    <Link className={classes.link} title={title} to={to}>
      <em className={iconClass}></em>
      <span>{children}</span>
      <div className="badge badge-primary">{count}</div>
    </Link>
  );
};

/** CTA temporário de troca para o Ambiente Agêntico */
const AccountSwitchCta = ({ href, title, avatarUrl, children }) => {
  const classes = accountSwitchCtaStyles();

  const [imgError, setImgError] = React.useState(false);

  const showAvatar = Boolean(avatarUrl) && !imgError;

  return (
    <li className={`sidebar-account-switch-item ${classes.item}`}>
      <a
        className={`sidebar-account-switch-btn ${classes.button}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={title}
      >
        {showAvatar ? (
          <img
            alt=""
            className={classes.avatar}
            src={avatarUrl}
            onError={() => setImgError(true)}
          />
        ) : (
          <em className={`fa fa-rocket ${classes.iconFallback}`} />
        )}
        <span className={`sidebar-account-switch-label ${classes.label}`}>{children}</span>
      </a>
    </li>
  );
};

/** Component to display headings on sidebar */
const SidebarItemHeader = ({ children }) => <li className="nav-heading">{children}</li>;

/** Normal items for the sidebar */
const SidebarItem = ({ children, isActive }) => (
  <li className={isActive ? 'active' : ''}>{children}</li>
);

class Sidebar extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      collapse: {
        home: this.routeActive(['home']),
        accountDashboard: this.routeActive(['account-dashboard']),
        jobs: this.routeActive(['jobs']),
        jobReview: this.routeActive(['job-review']),
        jobRequests: this.routeActive(['job-requests']),
        jobProfiles: this.routeActive(['job-profiles']),
        favorites: this.routeActive(['favorites']),
        reports: this.routeActive(['reports', 'diversity-reports']),
        applicants: this.routeActive(['hired', 'referral', 'internals']),
        interviews: this.routeActive(['interview-list']),
        settings: this.routeActive([
          'company-tree',
          'job-pipeline',
          'custom-test',
          'company-page',
          'job-tag-page',
          'applicant-tag-page',
          'talent-pool',
          'JobRequestApprovalFlowList',
          'Blocklist',
          'settings'
        ]),
        help: false,
        logout: this.routeActive(['logout'])
      },
      plan_type: '',
      use_approval_flow: false,
      jobsReviewCount: 0
    };
  }

  componentDidMount() {
    // pass navigator to access router api
    SidebarRun(this.navigator.bind(this), this.closeSidebar);
    this.getAccountInfo();

    if (UserProfile.isReviewer()) {
      this.getJobsReviewCount();
    }
    // Listen for routes changes in order to hide the sidebar on mobile
    this.props.history.listen(this.closeSidebarClose);
    this.setupAsideWidthSync();
  }

  componentDidUpdate() {
    this.scheduleAsideWidthSync();
  }

  componentWillUnmount() {
    this.teardownAsideWidthSync();
  }

  setupAsideWidthSync = () => {
    this.scheduleAsideWidthSync();

    if (typeof ResizeObserver !== 'undefined' && this.asideEl) {
      const nav = this.asideEl.querySelector('.sidebar-nav');
      if (nav) {
        this._asideResizeObserver = new ResizeObserver(() => {
          this.scheduleAsideWidthSync();
        });
        this._asideResizeObserver.observe(nav);
      }
    }
  };

  teardownAsideWidthSync = () => {
    if (this._asideWidthRaf) {
      cancelAnimationFrame(this._asideWidthRaf);
      this._asideWidthRaf = null;
    }

    if (this._asideResizeObserver) {
      this._asideResizeObserver.disconnect();
      this._asideResizeObserver = null;
    }
  };

  scheduleAsideWidthSync = () => {
    if (this._asideWidthRaf) cancelAnimationFrame(this._asideWidthRaf);
    this._asideWidthRaf = requestAnimationFrame(() => {
      this._asideWidthRaf = null;
      this.syncAsideWidth();
    });
  };

  /**
   * Largura = max(itens com link), entre 220 e 250px.
   * Ignora headings (scrollWidth herdava a largura do aside e inflava tudo).
   */
  syncAsideWidth = () => {
    const aside = this.asideEl;
    if (!aside) return;

    const wrapper = aside.closest('.wrapper');
    if (!wrapper) return;

    const body = document.body;
    if (
      body.classList.contains('aside-collapsed') ||
      body.classList.contains('aside-collapsed-text')
    ) {
      return;
    }

    const nav = aside.querySelector('.sidebar-nav');
    if (!nav) return;

    const ASIDE_MIN_WIDTH = 220;
    const ASIDE_MAX_WIDTH = 250;
    let maxContentWidth = ASIDE_MIN_WIDTH;

    const measureTextWidth = (el) => {
      if (!el) return 0;

      const range = document.createRange();
      range.selectNodeContents(el);
      const rects = range.getClientRects();
      let width = 0;
      for (let i = 0; i < rects.length; i += 1) {
        width = Math.max(width, rects[i].width);
      }

      return width;
    };

    nav.querySelectorAll(':scope > li').forEach((li) => {
      if (li.classList.contains('nav-heading')) return;

      const link = li.querySelector(':scope > a, :scope > .nav-item');
      if (!link) return;

      const liBorder = parseFloat(window.getComputedStyle(li).borderLeftWidth) || 0;
      const linkStyle = window.getComputedStyle(link);
      const padX =
        (parseFloat(linkStyle.paddingLeft) || 0) + (parseFloat(linkStyle.paddingRight) || 0);
      const marginX =
        (parseFloat(linkStyle.marginLeft) || 0) + (parseFloat(linkStyle.marginRight) || 0);
      const gap = parseFloat(linkStyle.columnGap || linkStyle.gap) || 0;

      const kids = Array.from(link.children).filter((child) => {
        const style = window.getComputedStyle(child);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });

      let childrenWidth = 0;
      kids.forEach((child) => {
        if (child.tagName === 'SPAN' || child.tagName === 'LABEL') {
          childrenWidth += measureTextWidth(child) || child.getBoundingClientRect().width;
        } else if (child.tagName === 'IMG') {
          childrenWidth += child.getBoundingClientRect().width;
        } else if (child.classList.contains('badge')) {
          childrenWidth += child.getBoundingClientRect().width;
        } else {
          const style = window.getComputedStyle(child);
          const declared = parseFloat(style.width);
          childrenWidth +=
            Number.isFinite(declared) && declared > 0
              ? declared
              : child.getBoundingClientRect().width;
        }
      });

      if (kids.length > 1 && gap > 0) {
        childrenWidth += gap * (kids.length - 1);
      }

      const measured = Math.ceil(padX + marginX + childrenWidth + liBorder + 4);
      if (Number.isFinite(measured) && measured > 0) {
        maxContentWidth = Math.max(maxContentWidth, measured);
      }
    });

    const nextWidth = Math.min(ASIDE_MAX_WIDTH, Math.max(ASIDE_MIN_WIDTH, maxContentWidth));
    const nextValue = `${nextWidth}px`;
    if (wrapper.style.getPropertyValue('--aside-wd').trim() !== nextValue) {
      wrapper.style.setProperty('--aside-wd', nextValue);
    }
  };

  closeSidebar = () => {
    this.props.actions.toggleSetting('asideToggled');
  };

  closeSidebarClose = () => {
    this.props.actions.closeSidebarSetting('asideToggled');
  };

  navigator(route) {
    this.props.history.push(route);
  }

  routeActive(paths) {
    paths = Array.isArray(paths) ? paths : [paths];
    const currPath = this.props.location.pathname;
    if (
      paths.findIndex((el) => {
        if (el.includes(currPath.replace('/', ''))) {
          return true;
        }

        return false;
      }) > -1
    )
      return true;

    return false;
  }

  toggleItemCollapse(stateName) {
    const collapse = { ...this.state.collapse };
    for (let c in collapse) {
      if (collapse[c] === true && c !== stateName) collapse[c] = false;
    }
    this.setState({
      collapse: {
        ...collapse,
        [stateName]: !this.state.collapse[stateName]
      }
    });
  }

  getAccountInfo() {
    let api = new Api();
    let filter = {
      fields: ['account_id', 'plan_type'],
      where: {
        account_id: UserProfile.getRealm()
      }
    };
    api.get('Accounts', filter, (status, data) => {
      const account = Array.isArray(data) && data.length > 0 ? data[0] : null;

      api.getParam('Accounts/use-approval-flow', {}, (statusFlow, use_approval_flow) => {
        this.setState({
          plan_type: (account && account.plan_type) || '',
          use_approval_flow: use_approval_flow === true
        });
      });
    });
  }

  getJobsReviewCount() {
    JobReviewController.getJobsToReview((err, jobsCount) => {
      if (err) {
        alert(err);
      } else {
        const jobsReviewCount = jobsCount;

        this.setState({
          jobsReviewCount: jobsReviewCount
        });
      }
    });
  }

  doLogout(e) {
    e.preventDefault();

    swal({
      icon: 'info',
      title: '',
      text: this.props.intl.formatMessage({ id: 'Sidebar.logoutMsg' }),
      buttons: {
        cancel: {
          text: this.props.intl.formatMessage({
            id: 'Base.button.cancel'
          }),
          value: null,
          visible: true,
          className: '',
          closeModal: true
        },
        confirm: {
          text: 'OK',
          value: true,
          visible: true,
          className: '',
          closeModal: true
        }
      }
    }).then((isConfirmed) => {
      if (isConfirmed) {
        let apiLogin = new ApiLogin();
        apiLogin.logout((err, data) => {
          if (err) {
            console.log(err);
          } else {
            LogoffRun();
            UserProfile.clear();
            this.props.history.push('/login');
          }
        });
      }
    });
  }

  render() {
    const planInfo = this.props.planInfo;
    const openRequestsCount = this.props.openRequestsCount;
    const jobsReviewCount = this.state.jobsReviewCount;

    const renderSettingsCompanyTree = () => {
      if (UserProfile.isAdmin()) {
        return (
          <li className={this.routeActive('company-tree') ? 'active' : ''}>
            <Link
              to="/company-tree"
              title={this.props.intl.formatMessage({
                id: 'Sidebar.settingsCompanyTree'
              })}
            >
              <small>
                <i className="fa fa-sitemap mr-2"></i>
                <FormattedMessage id="Sidebar.settingsCompanyTree" defaultMessage="Company Tree" />
              </small>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderSettingsJobTagPage = () => {
      if (UserProfile.isAdmin() || UserProfile.isTagUser()) {
        return (
          <li className={this.routeActive('job-tag-page') ? 'active' : ''}>
            <Link
              to="/job-tag-page"
              title={this.props.intl.formatMessage({
                id: 'Sidebar.settingsJobTagPage'
              })}
            >
              <small>
                <i className="fa fa-tag mr-2"></i>
                <FormattedMessage id="Sidebar.settingsJobTagPage" defaultMessage="Job Tags" />
              </small>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderSettingsApplicantTagPage = () => {
      if (UserProfile.isAdmin() || UserProfile.isTagUser()) {
        return (
          <li className={this.routeActive('applicant-tag-page') ? 'active' : ''}>
            <Link
              to="/applicant-tag-page"
              title={this.props.intl.formatMessage({
                id: 'Sidebar.applicantTagPage'
              })}
            >
              <small>
                <i className="fa fa-tag mr-2"></i>
                <FormattedMessage id="Sidebar.applicantTagPage" defaultMessage="Candidate Tags" />
              </small>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderSettingsCustomTest = () => {
      if (UserProfile.isAdmin() || UserProfile.isProcessManager()) {
        return (
          <li className={this.routeActive('custom-test') ? 'active' : ''}>
            <Link
              to="/custom-test"
              title={this.props.intl.formatMessage({
                id: 'Sidebar.settingsCustomTest'
              })}
            >
              <small>
                <i className="fa icon-calculator mr-2"></i>
                <FormattedMessage id="Sidebar.settingsCustomTest" defaultMessage="Forms" />
              </small>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderSettingsCareerPage = () => {
      if (UserProfile.isAdmin() || UserProfile.isPageAdmin()) {
        return (
          <li className={this.routeActive('company-page') ? 'active' : ''}>
            <Link
              to="/company-page"
              title={this.props.intl.formatMessage({
                id: 'Sidebar.settingsCareerPage'
              })}
            >
              <small>
                <i className="fa fa-newspaper mr-2"></i>
                <FormattedMessage id="Sidebar.settingsCareerPage" defaultMessage="Career Page" />
              </small>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderJobRequestApprovalFlowList = () => {
      const { accountInfo } = this.props;

      if (
        accountInfo &&
        accountInfo.accountInfo &&
        accountInfo.accountInfo.config_params &&
        accountInfo.accountInfo.config_params.use_approval_flow &&
        UserProfile.isAdmin()
      ) {
        return (
          <li className={this.routeActive('JobRequestApprovalFlowList') ? 'active' : ''}>
            <Link
              to="/job-requests-approval-flow"
              title={this.props.intl.formatMessage({
                id: 'Sidebar.jobRequestApprovalList'
              })}
            >
              <small>
                <i className="fas fa-edit mr-2"></i>
                <FormattedMessage id="Sidebar.jobRequestApprovalList" />
              </small>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderBlocklistUserAccount = () => {
      const { accountInfo } = this.props;

      if (
        accountInfo &&
        accountInfo.accountInfo &&
        accountInfo.accountInfo.blocklist_active &&
        (UserProfile.isAdmin() || UserProfile.isHRManager() || UserProfile.isRecruiter())
      ) {
        return (
          <li className={this.routeActive('Blocklist') ? 'active' : ''}>
            <Link
              to="/blocklist"
              title={this.props.intl.formatMessage({
                id: 'Sidebar.blockListUser'
              })}
            >
              <small>
                <i className="fa fa-lock mr-2"></i>
                <FormattedMessage id="Sidebar.blockListUser" />
              </small>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderSettingsUserAccount = () => {
      return (
        <li className={this.routeActive('settings') ? 'active' : ''}>
          <Link
            to="/settings"
            title={this.props.intl.formatMessage({
              id: 'Sidebar.settingsAccount'
            })}
          >
            <small>
              <i className="fa fa-cogs mr-2"></i>
              <FormattedMessage id="Sidebar.settingsAccount" defaultMessage="Account Settings" />
            </small>
          </Link>
        </li>
      );
    };

    const renderSettingsPipeline = () => {
      if (UserProfile.isAdmin() || UserProfile.isProcessManager()) {
        return (
          <li className={this.routeActive('job-pipeline') ? 'active' : ''}>
            <Link
              to="/job-pipeline"
              title={this.props.intl.formatMessage({ id: 'Sidebar.pipeline' })}
            >
              <small>
                <i className="fa icon-layers mr"></i>
                <FormattedMessage id="Sidebar.pipeline" defaultMessage="Manage Pipelines" />
              </small>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderSettings = () => {
      return (
        <li
          className={
            this.routeActive([
              'company-tree',
              'job-pipeline',
              'custom-test',
              'company-page',
              'settings'
            ])
              ? 'active'
              : ''
          }
        >
          <div
            className="nav-item"
            title={this.props.intl.formatMessage({ id: 'Sidebar.settings' })}
            onClick={this.toggleItemCollapse.bind(this, 'settings')}
          >
            <em className="icon-settings"></em>
            <span>
              <FormattedMessage id="Sidebar.settings" defaultMessage="Settings" />
            </span>
          </div>
          <Collapse isOpen={this.state.collapse.settings}>
            <ul id="#" className="sidebar-nav sidebar-subnav">
              <li className="sidebar-subnav-header">
                <FormattedMessage id="Sidebar.settings" defaultMessage="Settings" />
              </li>
              {renderSettingsCustomTest()}
              {renderSettingsPipeline()}
              {renderSettingsCareerPage()}
              {renderSettingsCompanyTree()}
              {renderSettingsJobTagPage()}
              {renderSettingsApplicantTagPage()}
              {renderJobRequestApprovalFlowList()}
              {renderBlocklistUserAccount()}
              {renderSettingsUserAccount()}
            </ul>
          </Collapse>
        </li>
      );
    };

    const renderJobPositions = () => {
      if (
        UserProfile.isAdmin() ||
        UserProfile.isHRManager() ||
        UserProfile.isRecruiter() ||
        UserProfile.isJobManager() ||
        UserProfile.isRequester() ||
        UserProfile.isAllocationManager()
      ) {
        let count = 0;
        if (planInfo.plan_jobs && planInfo.plan_jobs.count) {
          count = planInfo.plan_jobs.count;
        }

        return (
          <li className={this.routeActive(['jobs', 'job/add', 'closed-jobs']) ? 'active' : ''}>
            <SidebarBadgeLink
              count={count}
              iconClass="fa icon-bag"
              title={this.props.intl.formatMessage({ id: 'Sidebar.jobs' })}
              to="/jobs"
            >
              <FormattedMessage id="Sidebar.jobs" defaultMessage="Job Positions" />
            </SidebarBadgeLink>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderJobReviews = () => {
      if (UserProfile.isAdmin() || UserProfile.isReviewer()) {
        return (
          <li className={this.routeActive('job-review') ? 'active' : ''}>
            <Link
              to="/job-review"
              title={this.props.intl.formatMessage({
                id: 'Sidebar.jobsReviewPage'
              })}
            >
              {jobsReviewCount ? (
                <div className="float-right badge badge-primary">{jobsReviewCount}</div>
              ) : null}
              <em className="fa">
                <StyledRateReviewOutlinedIcon active={this.routeActive('job-review')} />
              </em>

              <span>
                <FormattedMessage id="Sidebar.jobsReviewPage" defaultMessage="Jobs Review" />
              </span>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderTalentSearch = () => {
      if (UserProfile.isAdmin() || UserProfile.isRecruiter() || UserProfile.isHRManager()) {
        return (
          <li className={this.routeActive('search') ? 'active' : ''}>
            <Link
              to="/search"
              title={this.props.intl.formatMessage({
                id: 'ModalSourcing.title'
              })}
              style={{
                padding: '12px 20px'
              }}
            >
              <em className="fa">
                <SearchIcon color={this.routeActive('search') ? 'primary' : 'inherit'} />
              </em>

              <span
                style={{
                  marginLeft: '4px'
                }}
              >
                <FormattedMessage id="ModalSourcing.title" />
              </span>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderJobRequests = () => {
      if ((this.state.plan_type || '').startsWith(CONST.PLAN.ENTERPRISE)) {
        if (this.state.use_approval_flow === true) {
          return (
            <li className={this.routeActive(['job-requests', 'job-request/add']) ? 'active' : ''}>
              <SidebarBadgeLink
                count={openRequestsCount}
                iconClass="fa icon-note"
                title={this.props.intl.formatMessage({
                  id: 'Sidebar.jobRequests'
                })}
                to="/job-requests"
              >
                <FormattedMessage id="Sidebar.jobRequests" defaultMessage="Job Requests" />
              </SidebarBadgeLink>
            </li>
          );
        } else {
          return null;
        }
      } else {
        return (
          <li className={this.routeActive(['job-requests', 'job-request/add']) ? 'active' : ''}>
            <a
              href="http://recrut.rds.land/fluxo-de-aprovacao"
              target="_blank"
              title={this.props.intl.formatMessage({
                id: 'Sidebar.jobRequests_noPlan'
              })}
              rel="noreferrer"
            >
              <div className="float-right badge badge-success" style={{ marginLeft: '10px' }}>
                <i className="fa fa-lock"></i>
              </div>
              <em className="fa icon-note"></em>

              <span>
                <FormattedMessage id="Sidebar.jobRequests" defaultMessage="Job Requests" />
              </span>
            </a>
          </li>
        );
      }
    };

    const renderJobProfiles = () => {
      if (UserProfile.isAdmin() || UserProfile.isProcessManager()) {
        return (
          <li
            className={
              this.routeActive(['job-profiles', 'job-profiles/add', 'job-profiles/edit'])
                ? 'active'
                : ''
            }
          >
            <Link
              to="/job-profiles"
              title={this.props.intl.formatMessage({
                id: 'Sidebar.jobProfiles'
              })}
            >
              <em className="fa icon-book-open"></em>
              <span>
                <FormattedMessage id="Sidebar.jobProfiles" defaultMessage="Job Profiles" />
              </span>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderAccountDashboard = () => {
      if (UserProfile.isAdmin() || UserProfile.isHRManager() || UserProfile.isRecruiter()) {
        return (
          <li className={this.routeActive(['account-dashboard']) ? 'active' : ''}>
            <Link
              to="/account-dashboard"
              title={this.props.intl.formatMessage({ id: 'Sidebar.accountDashboard' })}
            >
              <em className="fa icon-graph"></em>
              <span>
                <FormattedMessage
                  id="Sidebar.accountDashboard"
                  defaultMessage="Account Dashboard"
                />
              </span>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderJobReports = () => {
      if (!(UserProfile.isAdmin() || UserProfile.isHRManager() || UserProfile.isRecruiter())) {
        return null;
      }

      return (
        <li className={this.routeActive(['reports']) ? 'active' : ''}>
          <Link to="/job-reports" title={this.props.intl.formatMessage({ id: 'Sidebar.reports' })}>
            <small>
              <i className="fa icon-chart mr-2"></i>
              <FormattedMessage id="Sidebar.jobReports" defaultMessage="Job Reports" />
            </small>
          </Link>
        </li>
      );
    };

    const renderDiversityReports = () => {
      if (!(UserProfile.isAdmin() || UserProfile.isDiversity())) {
        return null;
      }

      return (
        <li className={this.routeActive(['']) ? 'active' : ''}>
          <Link
            to="/diversity-reports"
            title={this.props.intl.formatMessage({ id: 'Sidebar.diversityReports' })}
          >
            <small>
              <i className="fa icon-chart mr-2"></i>
              <FormattedMessage id="Sidebar.diversityReports" defaultMessage="Diversity Reports" />
            </small>
          </Link>
        </li>
      );
    };

    const renderAccountReports = () => {
      if (
        UserProfile.isAdmin() ||
        UserProfile.isHRManager() ||
        UserProfile.isRecruiter() ||
        UserProfile.isDiversity()
      ) {
        return (
          <li className={this.routeActive(['']) ? 'active' : ''}>
            <div
              className="nav-item"
              title={this.props.intl.formatMessage({ id: 'Sidebar.reports' })}
              onClick={this.toggleItemCollapse.bind(this, 'reports')}
            >
              <em className="fa icon-chart"></em>
              <span>
                <FormattedMessage id="Sidebar.reports" defaultMessage="Reports" />
              </span>
            </div>
            <Collapse isOpen={this.state.collapse.reports}>
              <ul id="#" className="sidebar-nav sidebar-subnav">
                <li className="sidebar-subnav-header">
                  <FormattedMessage id="Sidebar.reports" defaultMessage="Reports" />
                </li>
                {renderJobReports()}
                {renderDiversityReports()}
              </ul>
            </Collapse>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderHiredList = () => {
      if (
        UserProfile.isAdmin() ||
        UserProfile.isHRManager() ||
        UserProfile.isRecruiter() ||
        UserProfile.isJobManager()
      ) {
        return (
          <li className={this.routeActive(['']) ? 'active' : ''}>
            <Link to="/hired-list" title={this.props.intl.formatMessage({ id: 'Sidebar.Hired' })}>
              <small>
                <em className="fa fa icon-trophy mr-2"></em>
                <FormattedMessage id="Sidebar.Hired" defaultMessage="Hired" />
              </small>
            </Link>
          </li>
        );
      } else {
        return (
          <li className={this.routeActive(['']) ? 'active' : ''}>
            <a className="disabled-link">
              <small>
                <i className="fa icon-chart mr-2"></i>
                <FormattedMessage id="Sidebar.Hired" />
              </small>
            </a>
          </li>
        );
      }
    };

    const renderReferralList = () => {
      if (
        UserProfile.isAdmin() ||
        UserProfile.isHRManager() ||
        UserProfile.isRecruiter() ||
        UserProfile.isJobManager()
      ) {
        return (
          <li className={this.routeActive(['']) ? 'active' : ''}>
            <Link
              to="/referral-list"
              title={this.props.intl.formatMessage({ id: 'Sidebar.Referral' })}
            >
              <small>
                <i className="far fa-address-book mr-2"></i>
                <FormattedMessage id="Sidebar.Referral" defaultMessage="Referrals" />
              </small>
            </Link>
          </li>
        );
      } else {
        return (
          <li className={this.routeActive(['']) ? 'active' : ''}>
            <a className="disabled-link">
              <small>
                <i className="fa icon-chart mr-2"></i>
                <FormattedMessage id="Sidebar.Referral" />
              </small>
            </a>
          </li>
        );
      }
    };

    const renderApplicantInternalList = () => {
      if (
        UserProfile.isAdmin() ||
        UserProfile.isHRManager() ||
        UserProfile.isRecruiter() ||
        UserProfile.isJobManager()
      ) {
        return (
          <li className={this.routeActive(['']) ? 'active' : ''}>
            <Link
              to="/internals-list"
              title={this.props.intl.formatMessage({ id: 'Sidebar.ApplicantInternalList' })}
            >
              <small>
                <i className="far fa-address-card mr-2"></i>
                <FormattedMessage id="Sidebar.ApplicantInternalList" defaultMessage="Internal" />
              </small>
            </Link>
          </li>
        );
      } else {
        return (
          <li className={this.routeActive(['']) ? 'active' : ''}>
            <a className="disabled-link">
              <small>
                <em className="far fa-user  mr-2"></em>
                <FormattedMessage id="Sidebar.ApplicantInternalList" />
              </small>
            </a>
          </li>
        );
      }
    };

    const renderAccountApplicants = () => {
      if (
        UserProfile.isAdmin() ||
        UserProfile.isHRManager() ||
        UserProfile.isRecruiter() ||
        UserProfile.isJobManager()
      ) {
        return (
          <li className={this.routeActive(['']) ? 'active' : ''}>
            <div
              className="nav-item"
              title={this.props.intl.formatMessage({ id: 'Sidebar.Candidates' })}
              onClick={this.toggleItemCollapse.bind(this, 'applicants')}
            >
              <em className="far fa-user"></em>
              <span>
                <FormattedMessage id="Sidebar.Candidates" defaultMessage="Candidates" />
              </span>
            </div>
            <Collapse isOpen={this.state.collapse.applicants}>
              <ul id="#" className="sidebar-nav sidebar-subnav">
                <li className="sidebar-subnav-header">
                  <FormattedMessage id="Sidebar.Candidates" />
                </li>
                {renderHiredList()}
                {renderReferralList()}
                {renderApplicantInternalList()}
              </ul>
            </Collapse>
          </li>
        );
      } else {
        return null;
      }
    };

    const renderInterviewsList = () => {
      if (
        UserProfile.isAdmin() ||
        UserProfile.isHRManager() ||
        UserProfile.isRecruiter() ||
        UserProfile.isJobManager()
      ) {
        return (
          <li className={this.routeActive(['interview-list']) ? 'active' : ''}>
            <Link
              to="/interview-list"
              title={this.props.intl.formatMessage({ id: 'Sidebar.ApplicantsInListInterview' })}
            >
              <em className="far fa-calendar"></em>
              <span>
                <FormattedMessage
                  id="Sidebar.ApplicantsInListInterview"
                  defaultMessage="Appointments"
                />
              </span>
            </Link>
          </li>
        );
      } else {
        return null;
      }
    };

    // FEAT TEMPORÁRIA: botão de troca para o Ambiente Agêntico (RD)
    const renderAccountSwitch = () => {
      const account = (this.props.accountInfo && this.props.accountInfo.accountInfo) || {};
      const switchConfig = getAccountSwitchConfig(
        account.unique_name,
        account.account_id,
        UserProfile.getRealm()
      );
      if (!switchConfig) return null;

      return (
        <AccountSwitchCta
          avatarUrl={switchConfig.avatarUrl}
          href={switchConfig.url}
          title={this.props.intl.formatMessage({
            id: 'Sidebar.agenticEnvironment',
            defaultMessage: 'Ambiente Agêntico'
          })}
        >
          <FormattedMessage id="Sidebar.agenticEnvironment" defaultMessage="Ambiente Agêntico" />
        </AccountSwitchCta>
      );
    };

    return (
      <aside
        className="aside-container"
        ref={(el) => {
          this.asideEl = el;
        }}
      >
        {/* START Sidebar (left) */}
        <div className="aside-inner">
          <nav data-sidebar-anyclick-close="" className="sidebar">
            {/* START sidebar nav */}
            <ul className="sidebar-nav">
              {/* Iterates over all sidebar items */}
              <SidebarItemHeader>
                <span>
                  <FormattedMessage id="Sidebar.mainNavigation" defaultMessage="Main Navigation" />
                </span>
              </SidebarItemHeader>
              <SidebarItem isActive={this.routeActive('home')}>
                <Link
                  to="/home"
                  title={this.props.intl.formatMessage({
                    id: 'Sidebar.home'
                  })}
                >
                  <em className="icon-home"></em>
                  <span>
                    <FormattedMessage id="Sidebar.home" defaultMessage="Home" />
                  </span>
                </Link>
              </SidebarItem>

              {renderAccountSwitch()}

              {renderJobPositions()}

              {renderJobReviews()}

              {renderJobRequests()}

              {renderJobProfiles()}

              {renderTalentSearch()}

              {renderAccountReports()}
              {renderAccountApplicants()}
              {renderInterviewsList()}
              {renderAccountDashboard()}

              {renderSettings()}

              <li className={this.routeActive([]) ? 'active' : ''}>
                <a
                  href="https://meajuda.recrut.ai/hc/"
                  target="_kb"
                  title={this.props.intl.formatMessage({
                    id: 'Sidebar.help'
                  })}
                >
                  <em className="icon-question"></em>
                  <span>
                    <FormattedMessage id="Sidebar.help" defaultMessage="Help Center" />
                  </span>
                </a>
              </li>
              <li className={this.routeActive('logout') ? 'active' : ''}>
                <a
                  onClick={(e) => this.doLogout(e)}
                  title={this.props.intl.formatMessage({
                    id: 'Sidebar.logout'
                  })}
                >
                  <em className="icon-logout"></em>
                  <span>
                    <FormattedMessage id="Sidebar.logout" defaultMessage="Logout" />
                  </span>
                </a>
              </li>
            </ul>
            {/* END sidebar nav */}
          </nav>
        </div>
        {/* END Sidebar (left) */}
      </aside>
    );
  }
}

Sidebar.propTypes = {
  actions: PropTypes.object,
  settings: PropTypes.object,
  planInfo: PropTypes.object,
  accountInfo: PropTypes.object
};

const mapStateToProps = (state) => ({
  settings: state.settings,
  openRequestsCount: state.requests.openRequestsCount,
  planInfo: state.planInfo.accountPlan,
  accountInfo: state.accountInfo
});
const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(injectIntl(Sidebar)));
